import { Fragment, useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import { EarningsCardProps } from './earnings-card.types.ts';
import { isWebView } from '@/utility/webview.ts';
import clsx from 'clsx';

const TOOLTIP_CONTENTS = {
	UNRATED: 'Puanlanmamış görüşmeler, ay sonunda aylık ortalama puan baz alınarak hesaplanır.',
	NOT_PARTICIPATED: "Katılım olmayan görüşme oranınız ay sonunda hesaplanır ve %10'una kadar ödeme yapılır.",
	UNRATED_DETAIL: 'Puanlanmamış anlık görüşmeniz, aylık ortalama anlık puanınıza göre, puanlanmamış randevulu görüşmeniz ise aylık ortalama randevulu puanınıza göre değerlendirilmiştir.',
	NOT_PARTICIPATED_DETAIL: "Katılım olmayan görüşme oranınızın %10'u kazancınıza eklenmiştir."
};

const DOT_COLORS = {
	RATED: 'var(--brand-fill)',
	UNRATED: '#95A5A6',
	NOT_PARTICIPATED: 'var(--danger-fill)',
	PREVIOUS_MONTH: '#1ABC9C',
	BONUS: '#1ABC9C',
	BONUS_INACTIVE: 'var(--danger-fill)',
	YELLOW: '#EACB30'
};

const formatNumber = (value: number) => (isNaN(value) ? 0 : value);
const getDecimalPart = (number: number) => {
	const value = isNaN(number) ? 0 : number;
	return (value % 1).toFixed(2).substring(2);
};
const getIntegerPart = (number: number) => {
	const value = isNaN(number) ? 0 : number;
	return Math.floor(value);
};

export const EarningsCard = ({ expertAverageRating, expertPayments, activeView, hasAppointmentService = false, hasGotItAppointmentService = false, onOpenMonthlyEarningModal }: EarningsCardProps) => {
	const { fullPaymentAmount = 0, notRatedPaymentAmount = 0, partialPaymentAmount = 0, previousMonthsTransferAmount = 0, minimumPaymentAmount = 0, manuelPaymentList = [] } = expertPayments || {};

	const totalManuelPayment = useMemo(() => manuelPaymentList.reduce((total, manuelPayment) => total + (manuelPayment?.totalPaymentAmount || 0), 0), [manuelPaymentList]);

	const totalPayment = useMemo(
		() => fullPaymentAmount + notRatedPaymentAmount + partialPaymentAmount + previousMonthsTransferAmount + totalManuelPayment,
		[fullPaymentAmount, notRatedPaymentAmount, partialPaymentAmount, previousMonthsTransferAmount, totalManuelPayment]
	);

	const isCoachingView = activeView === 'COACHING';
	const isSolverView = activeView === 'SOLVER';
	const isGotItView = activeView === 'GOT_IT';

	const renderNotRatedTooltip = (tooltipId, tooltipContent) => (
		<Fragment>
			<div className="tooltip-wrapper">
				<i
					className="icons-info-circle"
					data-tooltip-id={tooltipId}
				/>
				<span>Hesaplanmadı</span>
			</div>
			<Tooltip
				id={tooltipId}
				className="tooltip-container"
				place="top-start"
				render={() => <div>{tooltipContent}</div>}
			/>
		</Fragment>
	);

	const renderNotRatedValue = (tooltipId, tooltipContent, amount) => (
		<Fragment>
			<span className="value">
				<i
					className="icons-info-circle"
					data-tooltip-id={tooltipId}
				/>
				₺{formatNumber(amount)}
			</span>
			<Tooltip
				id={tooltipId}
				className="tooltip-container"
				place="top-start"
				render={() => <div>{tooltipContent}</div>}
			/>
		</Fragment>
	);

	const renderManuelPaymentList = () =>
		manuelPaymentList?.map((manuelPayment, index) => (
			<div
				className="detail-item"
				key={index}>
				<div className="label">
					<span
						className="dot"
						style={{ backgroundColor: DOT_COLORS.YELLOW }}
					/>
					<span>{manuelPayment?.monthlyPaymentSubTypeDesc || ''}</span>
				</div>
				<span className="value">
					<i
						className="icons-info-circle"
						data-tooltip-id={`manuel-payment-${index}`}
					/>
					₺{formatNumber(manuelPayment?.totalPaymentAmount || 0)}
				</span>
			</div>
		));

	const getInstantRatingLabel = () => {
		if (isSolverView) return 'Çözücü Ortalama Puan:';
		if (isWebView()) return 'Anlık Ort. Puan:';
		return 'Anlık Ortalama Puan:';
	};

	const instantRating = useMemo(() => {
		return expertAverageRating?.find(rating => rating?.liveServiceType === 'GUIDANCE' || rating?.liveServiceType === 'LECTURE' || rating?.liveServiceType === 'QUESTION_SOLUTION') || null;
	}, [expertAverageRating]);

	const appointmentRating = useMemo(() => {
		return expertAverageRating?.find(rating => rating?.liveServiceType === 'ONE_ON_ONE_COACHING' || rating?.liveServiceType === 'ONE_ON_ONE_LECTURE') || null;
	}, [expertAverageRating]);

	const getAppointmentRatingLabel = () => {
		return isWebView() ? 'Randevulu Ort. Puan:' : 'Randevulu Ortalama Puan:';
	};

	const getNoRatingText = () => {
		if (activeView === 'SOLVER') {
			return 'Bu ay içerisinde henüz puanlanmış bir soru çözümünüz bulunmamaktadır.';
		}

		if (!instantRating?.monthlyAvgRating && !appointmentRating?.monthlyAvgRating) {
			return 'Bu ay içerisinde henüz puanlanmış bir görüşmeniz bulunmamaktadır.';
		}

		if (!instantRating?.monthlyAvgRating) {
			return 'Bu ay içerisinde henüz puanlanmış bir anlık görüşmeniz bulunmamaktadır.';
		}

		if (!appointmentRating?.monthlyAvgRating) {
			return 'Bu ay içerisinde henüz puanlanmış bir randevulu görüşmeniz bulunmamaktadır.';
		}

		return 'Bu ay içerisinde henüz puanlanmış bir görüşmeniz bulunmamaktadır.';
	};

	const shouldShowAppointmentRating = (hasGotItAppointmentService && isGotItView) || (hasAppointmentService && isCoachingView);

	const isShowNoPointPlaceholderCondition = isSolverView
		? !instantRating?.monthlyAvgRating && !appointmentRating?.monthlyAvgRating
		: !instantRating?.monthlyAvgRating || !appointmentRating?.monthlyAvgRating;

	return (
		<div className="earnings-card">
			<div className="earnings-card-content">
				<div className="main-amount">
					<div>
						<span className="currency">₺</span>
						<span className="amount">{getIntegerPart(totalPayment)}</span>
						<span className="decimal">,{getDecimalPart(totalPayment)}</span>
					</div>
					<div className="notice">
						<i className="icons-info-circle" />
						<span>{minimumPaymentAmount} TL ve üzeri kazançların ödemesi her ay başında yapılmaktadır.</span>
					</div>
				</div>

				{isSolverView && (
					<div className="details">
						<div className="detail-item">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.RATED }}
								/>
								<span>Puanlanan yanıt</span>
							</div>
							<span className="value">₺{formatNumber(fullPaymentAmount)}</span>
						</div>

						<div className="detail-item">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.UNRATED }}
								/>
								<span>Puanlanmayan yanıt</span>
							</div>
							{!notRatedPaymentAmount ? (
								renderNotRatedTooltip('tooltip-solver-not-rated', TOOLTIP_CONTENTS.UNRATED)
							) : (
								<span className="value">₺{formatNumber(notRatedPaymentAmount)}</span>
							)}
						</div>

						<div className="detail-item has-bonus">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.PREVIOUS_MONTH }}
								/>
								<span>Geçen Aydan Aktarılan</span>
							</div>
							<span className="value">₺{formatNumber(previousMonthsTransferAmount)}</span>
						</div>

						{renderManuelPaymentList()}

						<button
							className="monthly-earning-button"
							onClick={onOpenMonthlyEarningModal}>
							<i className="icons-info-circle" />
							Aylık Kazanç Nasıl Hesaplanır?
						</button>
					</div>
				)}

				{isGotItView && (
					<div className="details">
						<div className="detail-item">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.RATED }}
								/>
								<span>Puanlanan görüşme</span>
							</div>
							<span className="value">₺{formatNumber(fullPaymentAmount)}</span>
						</div>

						<div className="detail-item">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.UNRATED }}
								/>
								<span>Puanlanmayan görüşme</span>
							</div>
							{!notRatedPaymentAmount
								? renderNotRatedTooltip('tooltip-coaching-not-rated', TOOLTIP_CONTENTS.UNRATED)
								: renderNotRatedValue('tooltip-coaching-not-rated-detail', TOOLTIP_CONTENTS.UNRATED_DETAIL, notRatedPaymentAmount)}
						</div>

						<div className="detail-item has-bonus">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.PREVIOUS_MONTH }}
								/>
								<span>Geçen Aydan Aktarılan</span>
							</div>
							<span className="value">₺{formatNumber(previousMonthsTransferAmount)}</span>
						</div>

						{renderManuelPaymentList()}
						<button
							className="monthly-earning-button"
							onClick={onOpenMonthlyEarningModal}>
							<i className="icons-info-circle" />
							Aylık Kazanç Nasıl Hesaplanır?
						</button>
					</div>
				)}

				{isCoachingView && (
					<div className="details">
						<div className="detail-item">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.RATED }}
								/>
								<span>Puanlanan görüşme</span>
							</div>
							<span className="value">₺{formatNumber(fullPaymentAmount)}</span>
						</div>

						<div className="detail-item">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.UNRATED }}
								/>
								<span>Puanlanmayan görüşme</span>
							</div>
							{!notRatedPaymentAmount
								? renderNotRatedTooltip('tooltip-coaching-not-rated', TOOLTIP_CONTENTS.UNRATED)
								: renderNotRatedValue('tooltip-coaching-not-rated-detail', TOOLTIP_CONTENTS.UNRATED_DETAIL, notRatedPaymentAmount)}
						</div>

						{hasAppointmentService && (
							<div className="detail-item">
								<div className="label">
									<span
										className="dot"
										style={{ backgroundColor: DOT_COLORS.NOT_PARTICIPATED }}
									/>
									<span>Katılım olmayan görüşme</span>
								</div>
								{!partialPaymentAmount
									? renderNotRatedTooltip('tooltip-coaching-not-participated', TOOLTIP_CONTENTS.NOT_PARTICIPATED)
									: renderNotRatedValue('tooltip-coaching-not-participated-detail', TOOLTIP_CONTENTS.NOT_PARTICIPATED_DETAIL, partialPaymentAmount)}
							</div>
						)}

						<div className="detail-item has-bonus">
							<div className="label">
								<span
									className="dot"
									style={{ backgroundColor: DOT_COLORS.PREVIOUS_MONTH }}
								/>
								<span>Geçen Aydan Aktarılan</span>
							</div>
							<span className="value">₺{formatNumber(previousMonthsTransferAmount)}</span>
						</div>

						{renderManuelPaymentList()}
						<button
							className="monthly-earning-button"
							onClick={onOpenMonthlyEarningModal}>
							<i className="icons-info-circle" />
							Aylık Kazanç Nasıl Hesaplanır?
						</button>
					</div>
				)}
			</div>

			{(instantRating?.monthlyAvgRating || appointmentRating?.monthlyAvgRating) && (
				<div
					className={clsx('rating-wrapper', {
						'rating-wrapper-padding': instantRating?.monthlyAvgRating && appointmentRating?.monthlyAvgRating,
						'mobile-single-view': (instantRating?.monthlyAvgRating && !appointmentRating?.monthlyAvgRating) || (!instantRating?.monthlyAvgRating && appointmentRating?.monthlyAvgRating)
					})}>
					{instantRating?.monthlyAvgRating && (
						<div className="rating-content">
							<p>{getInstantRatingLabel()}</p>
							<div className="point-wrapper">
								<i className="star icons-star" />
								<span>{instantRating?.monthlyAvgRating || '4'}</span>
							</div>
						</div>
					)}

					{shouldShowAppointmentRating && !!appointmentRating?.monthlyAvgRating && (
						<div className="rating-content">
							<p>{getAppointmentRatingLabel()}</p>
							<div className="point-wrapper">
								<i className="star icons-star" />
								<span>{appointmentRating?.monthlyAvgRating || '4'}</span>
							</div>
						</div>
					)}
				</div>
			)}

			{isShowNoPointPlaceholderCondition && (
				<div className="no-rating-wrapper">
					<i className="icons-info-circle" />
					<p>{getNoRatingText()}</p>
				</div>
			)}
		</div>
	);
};

export default EarningsCard;
